import { useCallback, useMemo, useState, memo, useEffect, useRef } from 'react';
import { Tooltip } from 'antd';
import CommRecordsDrawer from '../comm-records-drawer';
import CollectAction from '@/components/collect-action';
import { FullScreen, ExitFullScreen, NewCreateQuestionSvg } from '@/components/svg-icons/navigation-bar';
import { getGlobalStore, useGlobalStore } from '@/store/global';
import { useQaStore } from '@/store/qa';
import { getChatSseHeaders } from '../dialogue-area/index.helper';
import { environment } from '@gaodun.com/gtools';
import { createNewQuestion } from '../../index.helper';
import queryDomMethods from '@/utils/query-dom-methods';
import './index.scss';

const NavgationBar = () => {
    const { subscribe, dragDrawerWidth, setDragDrawerWidthForMax, setDragDrawerWidthForMin, dragDrawerMaxWidth } = useGlobalStore();
    const { qaConfig, updateQaConfig, addRequestController } = useQaStore();
    const {
        id: commId,
        transferLaborState,
        projectId,
        lucaId,
        projectName = '',
        subjectName = '',
        twoLevelSubjectName = '',
        questionTag,
        askChatTitle = '',
        showCreateNewQaBtn,
        aiTag,
        questionBotKey,
        lucaAskChatId,
        askId,
        refreshCommRecordsByEffect,
        collectFlag,
        isLucaAsk,
    } = qaConfig;
    const [visible, setVisibe] = useState(false);
    const [title, setTilte] = useState('');
    const [commRecordsIconShow, setCommRecordsIconShow] = useState(false);
    const timer = useRef<ReturnType<typeof setInterval>>();
    const abortController = useRef<AbortController>();

    const controlCommRecordsIconShow = useCallback((status: boolean) => {
        setCommRecordsIconShow(status);
    }, []);

    const handleCloseQaRecordDrawer = useCallback(() => {
        setVisibe(false);
    }, []);

    const simulateStreamReply = (content = '') =>
        new Promise(resolve => {
            let index = -1;
            timer.current = setInterval(() => {
                setTilte(oldText => {
                    if (index >= content.length - 1) {
                        resolve(clearInterval(timer.current));
                        timer.current = undefined;
                        return oldText;
                    }
                    index++;
                    if (index === 0) return content[index];
                    return oldText + content[index];
                });
            }, 100);
        });

    const handleControlRightsModalVisible = () => {
        getGlobalStore()?.publish('RightsModal', { type: 'open', params: { lucaId, projectId } });
    };

    const updateTitle = async (content: string) => {
        const newContent = content.replace(/\n/g, ' ').slice(0, 150);
        await simulateStreamReply(newContent);
        updateQaConfig({ askChatTitle: content });
    };

    const refineProblem = async (params: { content: string; questionBotKey: string; lucaAskChatId: string }, isCreateQa = false) => {
        try {
            isCreateQa && updateTitle(params.content);
            abortController.current = addRequestController<'fetch'>('fetch');
            const headers = await getChatSseHeaders(params.questionBotKey);
            const response: Response = await fetch(`${environment.getApigateWay()}/athena/api/v1/front/luca-ask/problem/summary`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ lucaAskChatId: params.lucaAskChatId, content: params.content }),
                signal: abortController.current.signal,
            });
            const { result } = await response.json();
            if (!result) return false;
            updateQaConfig({ questionTag: 1 });
            !isCreateQa && updateTitle(params.content);
            return true;
        } catch (error) {
            return error;
        }
    };

    const handleQaScreenSizeControl = (isFullScreen: boolean) => {
        isFullScreen ? setDragDrawerWidthForMin() : setDragDrawerWidthForMax();
    };

    useEffect(() => {
        subscribe('NavgationBar', async ({ type, content, isCreateQa, lucaAskChatId, excuteRef }) => {
            switch (type) {
                case 'refineProblem':
                case 'refineProblemFromOcr':
                    if (questionTag === 1 || !lucaAskChatId) return;
                    if (type === 'refineProblemFromOcr') {
                        updateQaConfig({ questionTag: 1 });
                        updateTitle(content);
                    } else {
                        const result = await refineProblem({ lucaAskChatId, questionBotKey: questionBotKey || '', content }, isCreateQa);
                        if (excuteRef) {
                            result === 'send_cancel' ? excuteRef.reject('send_cancel') : excuteRef.resolve(result);
                        }
                    }
                    break;
                default:
                    abortController.current?.abort('切换会话/关闭答疑组件取消请求');
                    timer.current && clearInterval(timer.current);
                    setVisibe(false);
                    setTilte('');
                    setCommRecordsIconShow(false);
                    break;
            }
        });
    }, [questionTag, questionBotKey]);

    useEffect(() => {
        setTilte(askChatTitle.replace(/\n/g, ' ').slice(0, 150));
    }, [askChatTitle]);

    const renderHeader = useMemo(() => {
        const isFullScreen = dragDrawerWidth >= dragDrawerMaxWidth;
        return (
            <div className="navigation-bar__header">
                {transferLaborState === 1 ? (
                    <div className="navigation-bar__teacher">
                        <div className="navigation-bar__avatar"></div>
                        <span>名师答疑</span>
                    </div>
                ) : (
                    <div className="navigation-bar__ai">
                        <div className="navigation-bar__ai-logo"></div>
                    </div>
                )}
                {aiTag === 1 && <div className="navigation-bar__recharge" onClick={handleControlRightsModalVisible} />}
                {showCreateNewQaBtn ? (
                    <div className="navigation-bar__create-new-question" onClick={createNewQuestion}>
                        <NewCreateQuestionSvg className="navigation-bar__create-new-question-icon" />
                        <span>新问题</span>
                    </div>
                ) : (
                    <CollectAction
                        className="navigation-bar__collect-container"
                        initState={collectFlag}
                        lucaCommId={isLucaAsk === 1 ? commId : undefined}
                        askId={isLucaAsk === 1 ? undefined : commId}
                        needToastInQa
                    />
                )}
                <Tooltip
                    title={isFullScreen ? '退出全屏' : '全屏'}
                    placement="bottomRight"
                    arrow={{ pointAtCenter: true }}
                    getPopupContainer={queryDomMethods.queryLucaQaRoot}
                    autoAdjustOverflow={false}
                >
                    <span className="navigation-bar__qa-screen-size-control" onClick={() => handleQaScreenSizeControl(isFullScreen)}>
                        {isFullScreen ? <ExitFullScreen /> : <FullScreen />}
                    </span>
                </Tooltip>
            </div>
        );
    }, [showCreateNewQaBtn, transferLaborState, lucaId, collectFlag, dragDrawerWidth, projectId, aiTag, commId]);

    const renderTopic = useMemo(() => {
        let typeStr = `${projectName}-${subjectName}`;
        if (twoLevelSubjectName) typeStr += `-${twoLevelSubjectName}`;
        return (
            <div className="navigation-bar__topic">
                {title ? (
                    <div className="navigation-bar__question-describe">
                        <div>{title}</div>
                    </div>
                ) : (
                    <div className="navigation-bar__question-type">{typeStr}</div>
                )}
                {commRecordsIconShow && (
                    <div className="navigation-bar__qa-records-btn" onClick={() => setVisibe(true)}>
                        全部
                    </div>
                )}
            </div>
        );
    }, [title, projectName, commRecordsIconShow]);

    return (
        <div className="navigation-bar">
            {renderHeader}
            {renderTopic}
            <CommRecordsDrawer
                visible={visible}
                lucaAskChatId={lucaAskChatId}
                askId={askId}
                askChatTitle={askChatTitle}
                refreshCommRecordsByEffect={refreshCommRecordsByEffect}
                onClose={handleCloseQaRecordDrawer}
                onControlCommRecordsIconShow={controlCommRecordsIconShow}
            />
        </div>
    );
};

export default memo(NavgationBar);
